import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Snackbar, Alert } from "@mui/material";
import Pagination from "@mui/material/Pagination";

import LeasecontrolUsuariosToolbar from "../components/LeasecontrolUsuariosToolbar";
import LeasecontrolUsuariosTable from "../components/LeasecontrolUsuariosTable";
import LeasecontrolUsuarioDialog from "../components/LeasecontrolUsuarioDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import UsuarioIntegracionDetalleDialog from "../components/UsuarioIntegracionDetalleDialog";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";

import { lcGetUsuarioDetalle, lcListUsuarios, lcDeleteUsuario } from "../services/leasecontrolUsuarios.service";

export default function LeasecontrolUsuariosPage() {
  // data mode
  const [rows, setRows] = useState([]);     // modo normal
  const [groups, setGroups] = useState([]); // modo agrupado
  const [groupBy, setGroupBy] = useState(false); // toggle

  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState("");

  // UI state
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(1);

  // server pagination
  const [totalPages, setTotalPages] = useState(1);

  const [dialog, setDialog] = useState({ open: false, mode: "create", user: null });
  const [del, setDel] = useState({ open: false, user: null, loading: false });
  const [detalle, setDetalle] = useState({ open: false, loading: false, data: null });

  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });

  // Debounce para no spamear el backend
  const debounceRef = useRef(null);

  const fetchRows = async ({ usePage = page, useSearch = search, useLimit = limit, useGroupBy = groupBy } = {}) => {
    setErr("");
    setLoadingList(true);
    try {
      const resp = await lcListUsuarios({
        search: useSearch,
        page: usePage,
        limit: useLimit,
        groupBy: useGroupBy ? 1 : 0,
      });

      const payload = resp?.data;

      if (useGroupBy) {
        setGroups(payload?.data || []);
        setRows([]); // limpiamos para evitar estados mezclados
      } else {
        setRows(payload?.data || []);
        setGroups([]);
      }

      setTotalPages(payload?.totalPages || 1);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Error listando usuarios LeaseControl");
    } finally {
      setLoadingList(false);
    }
  };

  // primera carga y cuando cambia page/limit/groupBy
  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, groupBy]);

  // search debounce, tambien respeta groupBy
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchRows({ usePage: 1, useSearch: search, useLimit: limit, useGroupBy: groupBy });
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDialogSuccess = () => {
    setSnack({ open: true, type: "success", msg: "Guardado correctamente." });
    fetchRows();
  };

  const handleDeleteConfirm = async () => {
    const u = del.user;
    const itemId = u?.id ?? u?.userId ?? u?.itemId;
    if (!itemId) {
      setSnack({ open: true, type: "error", msg: "No se encontro itemId." });
      return;
    }

    try {
      setDel((s) => ({ ...s, loading: true }));
      await lcDeleteUsuario(Number(itemId));
      setSnack({ open: true, type: "success", msg: "Eliminado correctamente." });
      setDel({ open: false, user: null, loading: false });
      fetchRows();
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e.message || "Error eliminando",
      });
      setDel((s) => ({ ...s, loading: false }));
    }
  };

  const handleOpenDetalle = async (u) => {
    const itemId = u?.id ?? u?.userId ?? u?.itemId;
    if (!itemId) {
      setSnack({ open: true, type: "error", msg: "No se encontro itemId." });
      return;
    }

    setDetalle({ open: true, loading: true, data: null });
    try {
      const resp = await lcGetUsuarioDetalle(Number(itemId));
      setDetalle({ open: true, loading: false, data: resp?.data || null });
    } catch (e) {
      setSnack({ open: true, type: "error", msg: e?.response?.data?.error || e.message || "No se pudo cargar el detalle." });
      setDetalle({ open: false, loading: false, data: null });
    }
  };

  const showPagination = useMemo(() => totalPages > 1, [totalPages]);

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "calc(100vh - 64px)", bgcolor: "background.default" }}>
        <PageHeader title="Gestion de Usuarios" subtitle="Modulo de Leasecontrol" backTo=".." sx={{ mb: 1 }} />

        {/* Toolbar (tiene que soportar groupBy toggle)
            Si tu toolbar aun no tiene switch, igual puedes mostrarlo dentro del toolbar.
            Para no romper, lo pasamos como props opcionales. */}
        <LeasecontrolUsuariosToolbar
          search={search}
          onSearchChange={setSearch}
          limit={limit}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          onRefresh={() => fetchRows({ usePage: page, useSearch: search, useLimit: limit, useGroupBy: groupBy })}
          onCreate={() => setDialog({ open: true, mode: "create", user: null })}
          loading={loadingList}
          // nuevos (si tu toolbar no los usa, no pasa nada)
          groupBy={groupBy}
          onGroupByChange={(v) => {
            setGroupBy(v);
            setPage(1);
          }}
        />

        {!!err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1, mt: 2, opacity: loadingList ? 0.6 : 1 }}>
          <LeasecontrolUsuariosTable
            // si groupBy, mandamos groups, sino rows
            rows={!groupBy ? rows : []}
            groups={groupBy ? groups : null}
            onEdit={(u) => setDialog({ open: true, mode: "edit", user: u })}
            onDelete={(u) => setDel({ open: true, user: u, loading: false })}
            onDetail={handleOpenDetalle}
          />
        </Paper>

        {showPagination && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}

        <LeasecontrolUsuarioDialog
          open={dialog.open}
          mode={dialog.mode}
          initialUser={dialog.user}
          onClose={() => setDialog({ open: false, mode: "create", user: null })}
          onSuccess={handleDialogSuccess}
        />

        <ConfirmDeleteDialog
          open={del.open}
          loading={del.loading}
          description={`Seguro que deseas eliminar el usuario ID ${del.user?.id ?? del.user?.userId ?? del.user?.itemId}?`}
          onClose={() => setDel({ open: false, user: null, loading: false })}
          onConfirm={handleDeleteConfirm}
        />

        <UsuarioIntegracionDetalleDialog
          open={detalle.open}
          loading={detalle.loading}
          data={detalle.data}
          onClose={() => setDetalle({ open: false, loading: false, data: null })}
        />

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snack.type}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
